/**
 * Bağlantılar ekranının TEK durum sözlüğü (v3.3, PM § 5). Belge: docs/spec/castinflow/provider-connections.md § 13.
 *
 * İki ayrı soru vardır ve ikisi birbirine karıştırılmaz:
 *   1. ÖZELLİK durumu — "bu bağlantı türü bu ortamda bağlanabilir mi, değilse GERÇEK sebep ne?" (katalog + dört bağlama bölümü)
 *   2. BAĞLANTI durumu — "var olan bağlantı kullanılabilir mi?" (bağlantı kartı)
 *
 * Özellik durumu (`coming_soon` | `not_configured` | `pending_review` | `available`):
 *   Yakında          kodu teslim edilmemiş tür. Kodu teslim edilmiş bir özellik için ASLA kullanılmaz
 *                    (`PROVIDER_CODE_DELIVERED` tek kaynaktır).
 *   Yapılandırılmadı kod var; bu ortamda sağlayıcı yapılandırması eksik (bayrak kapalı / uygulama kimliği, sır, yapılandırma
 *                    kimliği yok). Sahibi: platform yöneticisi.
 *   Onay bekliyor    yapılandırma var; sağlayıcı incelemesi, uygulama doğrulaması ya da erişim seviyesi onayı BEKLENİYOR.
 *                    Bu bir dış gerçektir: sunucu bilemez, operatörün ortam yapılandırmasıyla BEYAN edilir. Beyan yoksa
 *                    "Onay bekliyor" denmez.
 *   (available)      bağlanabilir; rozet yok. Beyan eksikliği gibi bilgi notları `reasons` içinde taşınabilir.
 * Bağlantı durumu (`connected` | `authorization_expired` | `disconnected` | `verifying` | `error`):
 *   Bağlı · Yetki süresi doldu (iptal / süre bitti; yeniden yetkilendirme gerekir) · Bağlantı kesildi (kullanıcı kesti; yerel
 *   yetki yok) · Doğrulanıyor (belirteç henüz doğrulanmadı / sağlayıcı yanıtı bekleniyor) · Hata (sağlayıcı hesabı
 *   kullanılamıyor). v3.4'ten beri "Doğrulanıyor" ve "Hata" da durum modelinin PARÇASIDIR (yapay bir sınır yoktur): her durumun
 *   sebep kodları ve izinli geçişleri TEK tabloda (`PROVIDER_CONNECTION_STATE_MODEL`) tanımlıdır ve gerçek geçiş kuralıyla
 *   (`PROVIDER_STATUS_TRANSITIONS`) testle kilitlidir.
 *
 * Durum ve SEBEBİ sunucuda yapılandırmadan türetilir ve yanıtta taşınır; istemci tahmin etmez. Hiçbir alan sır, istemci kimliği
 * ya da iç yapılandırma adı taşımaz: sebep kodları kapalı kümedendir.
 */
import { z } from 'zod';
import { AD_SYNC_BUDGET_WAIT_CODE } from './google-ads-budget.js';
// ---------------------------------------------------------------------------------------------------------------------
// Özellik durumu
// ---------------------------------------------------------------------------------------------------------------------
/** Bağlantılar kataloğundaki türler (simülatör katalogda yoktur). Sıra ekran sırasıdır. */
export const PROVIDER_FEATURE_KINDS = [
    'whatsapp_waba',
    'meta_business',
    'meta_page',
    'meta_ads_account',
    'google_ads_customer',
];
/**
 * Türün KODU teslim edildi mi. "Yakında" yalnız `false` olan tür için söylenir. Bir tür için bağlama akışı teslim edildiğinde
 * burası `true` yapılır — durum metni elle değiştirilmez (v3.2'de arama reklamları kodu teslim edilmişken "Yakında" yazıyordu).
 */
export const PROVIDER_CODE_DELIVERED = {
    whatsapp_waba: true,
    meta_business: false,
    meta_page: true,
    meta_ads_account: true,
    google_ads_customer: true,
};
export const PROVIDER_FEATURE_STATES = [
    'coming_soon',
    'not_configured',
    'pending_review',
    'available',
];
/** Operatörün ortamda beyan ettiği sağlayıcı inceleme durumu (sır değil). Beyan yoksa durum `null`dır. */
export const PROVIDER_REVIEW_DECLARATIONS = ['pending', 'approved'];
/**
 * Sebep kodları (kapalı küme):
 *   not_delivered            (Yakında) tür için kod henüz teslim edilmedi
 *   flag_off                 (Yapılandırılmadı) bu ortamda gerçek kip bayrağı kapalı
 *   config_incomplete        (Yapılandırılmadı) bayrak açık ama gereken yapılandırma tam değil
 *   provider_review_pending  (Onay bekliyor) sağlayıcının uygulama / izin incelemesi sürüyor — operatör beyanı
 *   access_level_test_only   (Onay bekliyor) arama reklamları erişim seviyesi yalnız test hesaplarına izin veriyor — operatör beyanı
 *   verification_pending     (Onay bekliyor) uygulama doğrulaması tamamlanmadı — operatör beyanı
 *   access_level_undeclared  (bilgi) erişim seviyesi bildirilmedi; en dar varsayım (yalnız test) uygulanır
 *   verification_undeclared  (bilgi) uygulama doğrulaması bildirilmedi
 */
export const PROVIDER_STATE_REASONS = [
    'not_delivered',
    'flag_off',
    'config_incomplete',
    'provider_review_pending',
    'access_level_test_only',
    'verification_pending',
    'access_level_undeclared',
    'verification_undeclared',
];
/** Durumu kimin ilerleteceği: ürün ekibi (kod) · platform yöneticisi (ortam yapılandırması) · sağlayıcı (inceleme) · kimse */
export const PROVIDER_STATE_OWNERS = ['product', 'platform_admin', 'provider', 'none'];
export const providerFeatureStatusSchema = z.object({
    kind: z.enum(PROVIDER_FEATURE_KINDS),
    state: z.enum(PROVIDER_FEATURE_STATES),
    /** İlk sebep birincildir; `available` durumunda boş ya da yalnız bilgi notları olabilir */
    reasons: z.array(z.enum(PROVIDER_STATE_REASONS)),
    owner: z.enum(PROVIDER_STATE_OWNERS),
});
export const providerStatesSchema = z.object({
    /** Katalogdaki her tür için tam olarak bir kayıt (`PROVIDER_FEATURE_KINDS` sırasıyla) */
    features: z.array(providerFeatureStatusSchema),
});
/** Özellik bağlanabilir mi (bağla düğmesi anlamlı). `pending_review` yalnız yapılandırma varken türetilir. */
export function providerFeatureConnectable(s) {
    return s.state === 'available' || s.state === 'pending_review';
}
/** Durumu birincil sebepten türetilmiş sahibine çevirir (sunucu bunu doldurur; testler tutarlılığı denetler) */
export function providerOwnerOf(state, reasons) {
    if (state === 'coming_soon')
        return 'product';
    if (state === 'not_configured')
        return 'platform_admin';
    if (state === 'pending_review')
        return 'provider';
    // available: yalnız beyan eksikliği notları varsa platform yöneticisi tamamlar
    return reasons.length > 0 ? 'platform_admin' : 'none';
}
/**
 * Eski sunucu yanıtı (`providerStates` yok) için DAR geri dönüş: yalnız sunucunun bayrak olarak bildirdiği `live` değerinden.
 * Bu yol "Onay bekliyor" ya da "Yakında" ÜRETMEZ (beyan yok; kod teslim edilmiş olabilir): kapalıysa "Yapılandırılmadı".
 * Yeni sunucu her zaman `providerStates` döndürür; bu yalnız sürüm farkı penceresi içindir.
 */
export function providerFeatureOf(list, kind) {
    const given = list.providerStates?.features.find((f) => f.kind === kind);
    if (given !== undefined)
        return given;
    if (!PROVIDER_CODE_DELIVERED[kind])
        return { kind, state: 'coming_soon', reasons: ['not_delivered'], owner: 'product' };
    const live = kind === 'meta_ads_account'
        ? list.adConnect?.live
        : kind === 'google_ads_customer'
            ? list.googleAdsConnect?.live
            : kind === 'meta_page'
                ? list.pageConnect?.live
                : undefined;
    return live === true
        ? { kind, state: 'available', reasons: [], owner: 'none' }
        : { kind, state: 'not_configured', reasons: ['flag_off'], owner: 'platform_admin' };
}
// ---------------------------------------------------------------------------------------------------------------------
// Bağlantı durumu
// ---------------------------------------------------------------------------------------------------------------------
export const PROVIDER_CONNECTION_STATES = [
    'connected',
    'authorization_expired',
    'disconnected',
    'verifying',
    'error',
];
/**
 * Durumun SEBEBİ (kapalı küme; her sebep tek bir duruma aittir — `PROVIDER_CONNECTION_STATE_MODEL`). Sunucu sakladığı alanlardan
 * türetir (satır durumu, durum gerekçesi, son / sonraki denetim); yeni sağlayıcı çağrısı yapılmaz, istemci tahmin etmez.
 *   Doğrulanıyor        check_queued         doğrulama işi kuyrukta: belirteç henüz hiç denetlenmedi
 *                       awaiting_provider    son denetimde sağlayıcıdan yanıt alınamadı (geçici); yeniden denenecek
 *   Bağlı               token_valid          belirteç geçerli
 *                       token_expiring       belirteç geçerli ama süresi yaklaşıyor
 *   Yetki süresi doldu  token_revoked        sağlayıcı yetkiyi kaldırdı ya da belirtecin süresi doldu
 *                       token_invalid        saklı belirteç geçersiz ya da eksik
 *   Hata                account_unavailable  sağlayıcı hesabı kullanılamıyor (hesap kapalı / erişilemiyor)
 *                       permission_missing   sağlayıcı izni eksik ya da geri alınmış
 *                       access_denied        sağlayıcı bu yetkiyle hesaba erişime izin vermiyor
 *                       check_failed         son denetim başarısız (başka / tanınmayan sağlayıcı kodu)
 *   Bağlantı kesildi    admin_disconnect     yönetici bağlantıyı kesti
 */
export const PROVIDER_CONNECTION_STATE_REASONS = [
    'check_queued',
    'awaiting_provider',
    'token_valid',
    'token_expiring',
    'token_revoked',
    'token_invalid',
    'account_unavailable',
    'permission_missing',
    'access_denied',
    'check_failed',
    'admin_disconnect',
];
/**
 * Bir geçişi neyin tetiklediği (kapalı küme):
 *   health_check        belirteç denetimi (oluşturma, "şimdi denetle", worker taraması)
 *   provider_rejection  sağlayıcı bir çağrıda belirteci / hesabı reddetti (gönderim, eşitleme, başvuru okuma …)
 *   reconnect           yeniden yetkilendirme (yeni belirteç) — `revoked` bağlantıyı YALNIZ bu açar; dönemsel denetim açmaz
 *   disconnect          yönetici bağlantıyı kesti (her durumdan; son durum)
 */
export const PROVIDER_CONNECTION_TRIGGERS = [
    'health_check',
    'provider_rejection',
    'reconnect',
    'disconnect',
];
/**
 * Bağlantı durumlarının TEK tablosu (v3.4): satır durumu eşlemesi + sebepler + izinli geçişler. Geçişler gerçek kuraldan ÖLÇÜLDÜ
 * (`PROVIDER_STATUS_TRANSITIONS` ve `provider-state.ts` `applyTokenCheck`) ve testle kilitlidir (`provider-status.test.ts`):
 *  - "Bağlı → Doğrulanıyor" YOKTUR: yeniden denetim durumu değiştirmez (geçici yanıtsızlık yalnız sağlığı `degraded` yapar);
 *    "Doğrulanıyor" yalnız yeni bağlantının ilk doğrulamasıdır.
 *  - "Yetki süresi doldu → Hata" YOKTUR; "Yetki süresi doldu → Bağlı" yalnız yeniden yetkilendirmeyle (`reconnect`) olur.
 *  - "Bağlantı kesildi" son durumdur: yeniden kullanmak yeni bir bağlantı kaydıdır (aynı kayıt geri açılmaz).
 */
export const PROVIDER_CONNECTION_STATE_MODEL = {
    verifying: {
        statuses: ['pending'],
        reasons: ['check_queued', 'awaiting_provider'],
        next: [
            { to: 'connected', via: ['health_check'] },
            { to: 'authorization_expired', via: ['health_check', 'provider_rejection'] },
            { to: 'error', via: ['health_check', 'provider_rejection'] },
            { to: 'disconnected', via: ['disconnect'] },
        ],
    },
    connected: {
        statuses: ['active', 'expiring'],
        reasons: ['token_valid', 'token_expiring'],
        next: [
            { to: 'authorization_expired', via: ['health_check', 'provider_rejection'] },
            { to: 'error', via: ['health_check', 'provider_rejection'] },
            { to: 'disconnected', via: ['disconnect'] },
        ],
    },
    authorization_expired: {
        statuses: ['revoked'],
        reasons: ['token_revoked', 'token_invalid'],
        next: [
            { to: 'connected', via: ['reconnect'] },
            { to: 'disconnected', via: ['disconnect'] },
        ],
    },
    error: {
        statuses: ['error'],
        reasons: ['account_unavailable', 'permission_missing', 'access_denied', 'check_failed'],
        next: [
            { to: 'connected', via: ['health_check', 'reconnect'] },
            { to: 'authorization_expired', via: ['health_check', 'provider_rejection'] },
            { to: 'disconnected', via: ['disconnect'] },
        ],
    },
    disconnected: {
        statuses: ['disconnected'],
        reasons: ['admin_disconnect'],
        next: [],
    },
};
/**
 * Bağlantı satırının durumu → sözlük durumu (saf; yapılandırmaya bağlı değildir):
 *   active, expiring → Bağlı (süresi yaklaşıyorsa ayrıca uyarılır) · revoked → Yetki süresi doldu · disconnected → Bağlantı
 *   kesildi · pending → Doğrulanıyor · error → Hata
 * Eşleme `PROVIDER_CONNECTION_STATE_MODEL.statuses` tablosundan okunur (her satır durumu tam bir sözlük durumuna aittir).
 */
export function providerConnectionStateOf(status) {
    const hit = PROVIDER_CONNECTION_STATES.find((st) => PROVIDER_CONNECTION_STATE_MODEL[st].statuses.includes(status));
    // Tablo her satır durumunu kapsar (testle kilitli); ulaşılamaz dal güvenli tarafta kalır
    return hit ?? 'error';
}
/** Sebebin ait olduğu durum (her sebep tek bir duruma aittir) */
export function providerConnectionStateOfReason(reason) {
    return (PROVIDER_CONNECTION_STATES.find((st) => PROVIDER_CONNECTION_STATE_MODEL[st].reasons.includes(reason)) ?? 'error');
}
/** Durum geçişi izinli mi (durum düzeyi). Aynı duruma "geçiş" geçiş sayılmaz */
export function canTransitionConnectionState(from, to) {
    return PROVIDER_CONNECTION_STATE_MODEL[from].next.some((n) => n.to === to);
}
/**
 * BEKLEME (hata DEĞİL): bağlantı "Bağlı" kalır; yalnız sağlayıcıdan okuma geçici olarak yapılamaz ve yer açılınca kendiliğinden
 * sürer. "Yetki süresi doldu" ya da "Hata" ile karıştırılmaz; yeniden yetkilendirme gerekmez.
 *   read_budget  sağlayıcının okuma hakkı (merkezi / çalışma alanı bütçesi ya da sağlayıcı sınırı) şu an dolu — arama
 *                reklamlarında bağlama akışı (`googleAdsConnect.budget.state = waiting`) ve harcama eşitlemesi
 *                (`provider_budget`) aynı bekleme nedenidir; metni tek kaynaktan gelir (`provider-wait-strings.ts`).
 */
export const PROVIDER_WAITS = ['read_budget'];
/** Harcama eşitlemesinin son hata kodundan bekleme nedeni (`provider_budget` → okuma hakkı); başka kod bekleme değildir */
export function providerWaitOfSyncCode(code) {
    return code === AD_SYNC_BUDGET_WAIT_CODE ? 'read_budget' : null;
}
/**
 * İş mesajlaşma hesabının KURULUM maddeleri (zorunlu olanlar): bunlardan biri tamam değilken hesap "bağlı" olabilir ama
 * "mesajlaşmaya hazır" DEĞİLDİR. `whatsapp-signup.ts` `WA_ONBOARDING_REQUIRED` ile aynı kümedir (test kilitler).
 */
export const PROVIDER_SETUP_ITEMS = [
    'permissions',
    'number_verified',
    'number_registered',
    'event_subscription',
    'messaging_health',
];
export const PROVIDER_SETUP_GAP_STATES = ['action_required', 'pending', 'unknown'];
export const providerConnectionSetupSchema = z.object({
    /** Zorunlu maddelerin HEPSİ tamam (okunamayan madde tamam sayılmaz) */
    complete: z.boolean(),
    /** Tamam olmayan zorunlu maddeler: eksik · sağlayıcı incelemesinde · okunamadı */
    gaps: z.array(z.object({ key: z.enum(PROVIDER_SETUP_ITEMS), state: z.enum(PROVIDER_SETUP_GAP_STATES) })),
});
export const providerConnectionDisplaySchema = z.object({
    state: z.enum(PROVIDER_CONNECTION_STATES),
    /**
     * v3.4: durumun SEBEBİ (kapalı küme; sunucu satırın durumu, gerekçesi ve son / sonraki denetiminden türetir). Sebep her zaman
     * durumunun sebeplerinden biridir (`PROVIDER_CONNECTION_STATE_MODEL`). Eski sunucu yanıtında yoktur (arayüz o zaman yalnız
     * durumu söyler, sebep uydurmaz).
     */
    reason: z.enum(PROVIDER_CONNECTION_STATE_REASONS).optional(),
    /**
     * v3.4: bir sonraki otomatik belirteç denetiminin zamanı — yalnız "Doğrulanıyor" ve "Hata" durumunda (yeniden deneme zamanı);
     * diğer durumlarda ya da zamanlanmış denetim yoksa `null`.
     */
    nextCheckAt: z.string().nullable().optional(),
    /**
     * Yalnız iş mesajlaşma hesabında ve yalnız `connected` iken dolu; diğer her durumda `null`. `complete = false` iken kart
     * "Bağlı — kurulum tamamlanmadı" der, "mesajlaşmaya hazır" DEMEZ.
     */
    setup: providerConnectionSetupSchema.nullable(),
});
/** Bağlantı kartı için sözlük durumu; sunucu `display` göndermediyse (eski yanıt) satırın durumundan türetilir */
export function providerConnectionDisplayOf(c) {
    return c.display ?? { state: providerConnectionStateOf(c.status), setup: null };
}
//# sourceMappingURL=provider-status.js.map